/**
 * @fileoverview A single navigable card with a colored icon tile on top, then
 * title and description. Shared by the Reports hub and School Settings so both
 * screens stay visual twins (same tile, typography, paddings and hover).
 *
 * `to` routes through react-router (SPA navigation). Legacy report screens live
 * on Rails routes outside the SPA, so pass `external` to do a full-page load.
 */

import { Card, Stack, Text, ThemeIcon } from '@mantine/core'
import { useNavigate } from 'react-router-dom'

interface ReportCardProps {
  readonly icon: React.ReactNode
  readonly title: string
  readonly description: string
  readonly to: string
  /** Icon background color (Mantine theme color). Defaults to cyan. */
  readonly color?: string
  /** Navigate with a full-page load instead of react-router (for Rails routes). */
  readonly external?: boolean
}

export function ReportCard({ icon, title, description, to, color = 'cyan', external = false }: ReportCardProps) {
  const navigate = useNavigate()

  const handleClick = () => {
    if (external) {
      window.location.assign(to)
    } else {
      navigate(to)
    }
  }

  return (
    <Card
      withBorder
      radius='md'
      p='lg'
      onClick={handleClick}
      sx={theme => ({
        cursor: 'pointer',
        transition: 'box-shadow 150ms ease, transform 150ms ease',
        '&:hover': {
          boxShadow: theme.shadows.sm,
          transform: 'translateY(-2px)',
        },
      })}
    >
      <Stack spacing='sm'>
        <ThemeIcon variant='light' color={color} radius='md' size={44}>
          {icon}
        </ThemeIcon>
        <Stack spacing={2}>
          <Text fw={600} size='lg'>
            {title}
          </Text>
          <Text color='dimmed' size='sm'>
            {description}
          </Text>
        </Stack>
      </Stack>
    </Card>
  )
}
